import { useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  SERJ_CLOUD_CYCLE_MS,
  SERJ_CLOUD_HOLD_MS,
  SERJ_CLOUD_OUT_MS,
  SERJ_CLOUD_STAGGER_MS,
} from './gameplayConstants';
import { SerjCloudModSvg } from './SerjCloudModSvg';
import './LevelSerjOverlay.css';

type SerjCloudPhase = 'active' | 'out';

const PHASE_MS: Record<SerjCloudPhase, number> = {
  active: SERJ_CLOUD_HOLD_MS,
  out: SERJ_CLOUD_OUT_MS,
};

const NEXT_PHASE: Record<SerjCloudPhase, SerjCloudPhase | null> = {
  active: 'out',
  out: null,
};

type LevelSerjOverlayProps = {
  isPaused: boolean;
  onComplete: () => void;
};

export function LevelSerjOverlay({
  isPaused,
  onComplete,
}: LevelSerjOverlayProps) {
  const [phase, setPhase] = useState<SerjCloudPhase>('active');
  const [shown, setShown] = useState(false);
  const remainingMsRef = useRef(PHASE_MS.active);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      setShown(true);
    });
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, []);

  useEffect(() => {
    if (phase === 'out') {
      setShown(false);
    }
  }, [phase]);

  useEffect(() => {
    if (isPaused) {
      return;
    }
    let frameId = 0;
    let lastNow = performance.now();
    const tick = (now: number) => {
      remainingMsRef.current -= now - lastNow;
      lastNow = now;
      if (remainingMsRef.current > 0) {
        frameId = window.requestAnimationFrame(tick);
        return;
      }
      const next = NEXT_PHASE[phase];
      if (next == null) {
        onCompleteRef.current();
        return;
      }
      remainingMsRef.current = PHASE_MS[next];
      setPhase(next);
    };
    frameId = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [phase, isPaused]);

  const className = [
    'level-serj-overlay',
    shown ? 'level-serj-overlay--shown' : '',
    phase === 'active' ? 'level-serj-overlay--active' : '',
    isPaused ? 'level-serj-overlay--paused' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={className}
      style={
        {
          '--serj-cloud-cycle-ms': `${SERJ_CLOUD_CYCLE_MS}ms`,
          '--serj-cloud-out-ms': `${SERJ_CLOUD_OUT_MS}ms`,
        } as CSSProperties
      }
      aria-hidden
    >
      <SerjCloudModSvg staggerMs={SERJ_CLOUD_STAGGER_MS} />
    </div>
  );
}
